import loadable from '@loadable/component'
import React, { ReactNode, useContext } from 'react'

import Gap from '@/common/components/Gap'
import Link from '@/common/components/Link'
import useDevice from '@/common/hooks/useDevice'
import useI18n, { Translation } from '@/common/hooks/useI18n'
import { N_DAYS } from '@/common/translation'
import { getTraslationObject } from '@/common/utils/object'
import { dateToString, numberToString } from '@/common/utils/string'
import { resolvePath } from '@/common/utils/url'
import { OwnerListingsActionContext } from '@/modules/me/pages/OwnerListingsPage/context'
import {
  LOCATION,
  ROOM_STATUS,
  ROOM_TYPE,
  AREA,
  BEDROOMS,
  BATHROOMS,
  FLOOR,
  PLACE_RATING,
  LAST_MODIFIED,
  ANNOUNCEMENT,
  RENEW,
  RENTAL_HISTORY,
  BROKER,
  BROKER_LIST,
} from '@/modules/me/pages/OwnerListingsPage/translation'
import {
  ME_OWNER_LISTING_RENTAL_HISTORY_PATH,
  ME_OWNER_LISTING_BORKER_LIST_PATH,
} from '@/modules/me/paths'
import {
  N_BEDROOMS,
  AVAILABLE,
  UNAVAILABLE,
} from '@/modules/property/translation'

import { Title, Value, DotStatus, RenewButton } from './styled'

const RoomViewsChart = loadable(
  () => import('@/modules/property/components/RoomViewsChart'),
  { ssr: false }
)

interface OwnerListingsMoreDetailProps {
  room: RentAPerfect.OwnerRoom
}

interface ItemProps {
  children: ReactNode
  title: Translation
}

const Item = ({ children, title }: ItemProps) => {
  const { t } = useI18n()

  return (
    <Gap className='align-start' space={10}>
      <Title>{t(title)}:</Title>
      <Value>{children}</Value>
    </Gap>
  )
}

const OwnerListingsMoreDetail = ({ room }: OwnerListingsMoreDetailProps) => {
  const { t } = useI18n()
  const { isMobile } = useDevice()
  const { setAction } = useContext(OwnerListingsActionContext)

  const { isApproved, isAvailable, RoomInfo: roomInfo, Condo: place } = room

  return (
    <Gap space={32} vertical>
      <Gap space={12} vertical>
        <Item title={LOCATION}>
          {place?.address
            ? t(getTraslationObject(place, 'address'))
            : place?.district
            ? t(getTraslationObject(place, 'district'))
            : '-'}
        </Item>
        <Item title={ROOM_STATUS}>
          <Gap className='align-center' space={4}>
            <DotStatus $isAvailable={isAvailable} />
            <div>{t(isAvailable ? AVAILABLE : UNAVAILABLE)}</div>
          </Gap>
        </Item>
        <Item title={ROOM_TYPE}>
          {roomInfo ? t(N_BEDROOMS, { n: roomInfo.roomNumber }) : '-'}
        </Item>
        <Item title={AREA}>
          {roomInfo ? numberToString(roomInfo.area, { decimals: 2 }) : '-'}
        </Item>
        <Item title={BEDROOMS}>
          {roomInfo ? numberToString(roomInfo.roomNumber) : '-'}
        </Item>
        <Item title={BATHROOMS}>
          {roomInfo ? numberToString(roomInfo.bathroomNumber) : '-'}
        </Item>
        <Item title={FLOOR}>
          {roomInfo ? numberToString(roomInfo.floor) : '-'}
        </Item>
        <Item title={PLACE_RATING}>
          {room.averageReview ? (
            <>
              {numberToString(room.averageReview, { decimals: 1 })}
              {room.voterNumber ? (
                <span className='text-gray ml-4'>
                  ({numberToString(room.voterNumber)})
                </span>
              ) : null}
            </>
          ) : (
            '-'
          )}
        </Item>
        <Item title={LAST_MODIFIED}>{dateToString(room.updatedAt)}</Item>
        {isMobile && isApproved ? (
          <Item title={RENTAL_HISTORY}>
            <Link
              className='text-primary text-semibold'
              to={resolvePath(ME_OWNER_LISTING_RENTAL_HISTORY_PATH, {
                roomId: room.id,
              })}
            >
              {t(RENTAL_HISTORY)}
            </Link>
          </Item>
        ) : null}
        {isMobile && isApproved ? (
          <Item title={BROKER}>
            <Link
              className='text-primary text-semibold'
              to={resolvePath(ME_OWNER_LISTING_BORKER_LIST_PATH, {
                roomId: room.id,
              })}
            >
              {t(BROKER_LIST)}
            </Link>
          </Item>
        ) : null}
        {isApproved ? (
          <Item title={ANNOUNCEMENT}>
            <RenewButton
              onClick={() => setAction({ name: 'renew_announcement', room })}
            >
              {t(RENEW)} ({t(N_DAYS, { n: room.remainingDay })})
            </RenewButton>
          </Item>
        ) : null}
      </Gap>
      {!isMobile && room.viewNumber ? (
        <RoomViewsChart
          monthlyViewByYear={room.monthlyViewByYear ?? {}}
          dailyViewByMonth={room.dailyViewByMonth ?? {}}
        />
      ) : null}
    </Gap>
  )
}

export default OwnerListingsMoreDetail
